import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../hooks/usePermissions';
import { useMenuCategory } from '../contexts/MenuCategoryContext';
import AppSubMenu from './AppSubMenu';

const AppMenu = () => {
    const [model, setModel] = useState([]);
    const { th, selectedCountry } = useLanguage();
    const { filterMenuItems } = usePermissions();
    const { selectedCategory } = useMenuCategory();

    useEffect(() => {
        // Eğer kategori seçilmemişse menü gösterme
        if (!selectedCategory) {
            setModel([]);
            return;
        }

        // Fetch the menu data from the JSON file
        fetch('/data/menuData.json', { cache: 'no-cache' })
            .then((response) => response.json())
            .then((data) => {
                // Önce menü verilerini yetki kontrolünden geçir
                const filteredData = filterMenuItems(data);

                // Seçili kategoriye göre menüyü filtrele
                const categoryMenu = getCategoryMenu(filteredData, selectedCategory);
                const transformedMenu = transformMenuData(categoryMenu);
                setModel(transformedMenu);
            })
            .catch((error) => {
                console.error('Error loading menu data:', error);
            });
    }, [selectedCountry, filterMenuItems, selectedCategory]); // selectedCategory dependency eklendi

    // Seçili kategoriye göre menüyü getir
    const getCategoryMenu = (menuData, categoryId) => {
        if (!menuData || menuData.length === 0) return [];

        // Ana menünün children'larından seçili kategoriyi bul
        const mainMenu = menuData[0];
        if (!mainMenu || !mainMenu.children) return [];

        const category = mainMenu.children.find((cat) => cat.id === categoryId);
        if (!category) return [];

        // Kategoriyi array olarak döndür ve children'larını aç
        return [
            {
                ...category,
                // Kategori başlığını label olarak kullan, children'ları da dahil et
                label: th(category.translationKey, { fallback: category.title }),
                items: category.children
            }
        ];
    };

    // Function to transform DMS menu format to PrimeReact menu format
    const transformMenuData = (menuData) => {
        // Map icon names from DMS format to PrimeReact icons
        const iconMap = {
            PhoneIphoneIcon: 'pi pi-fw pi-cog',
            CheckroomIcon: 'pi pi-fw pi-file',
            HomeIcon: 'pi pi-fw pi-home'
        };

        // Transform the menu data
        return menuData.map((item) => {
            const menuItem = {
                label: th(item.translationKey, { fallback: item.title }),
                icon: iconMap[item.icon || ''] || 'pi pi-fw pi-circle'
            };

            // Add link if available
            if (item.link) {
                menuItem.to = item.link;
            } else if (item.path) {
                menuItem.to = item.path;
            }

            // Process children if available
            if (item.children && item.children.length > 0) {
                menuItem.items = transformChildren(item.children);
            }

            return menuItem;
        });
    };

    // Function to transform child menu items
    const transformChildren = (children) => {
        return children.map((child) => {
            const childItem = {
                label: th(child.translationKey, { fallback: child.title }),
                icon:
                    child.id === 'phones'
                        ? 'pi pi-fw pi-tag'
                        : child.id === 'order-management'
                        ? 'pi pi-fw pi-chart-line'
                        : child.id === 'management'
                        ? 'pi pi-fw pi-desktop'
                        : child.id === 'products-search'
                        ? 'pi pi-fw pi-search'
                        : child.id === 'basket'
                        ? 'pi pi-fw pi-shopping-cart'
                        : child.id === 'warranty-management'
                        ? 'pi pi-fw pi-verified'
                        : child.id === 'computers4'
                        ? 'pi pi-fw pi-users'
                        : child.id === 'reports'
                        ? 'pi pi-fw pi-chart-bar'
                        : child.id === 'system-definitions'
                        ? 'pi pi-fw pi-cog'
                        : child.id === 'warranty-requests'
                        ? 'pi pi-fw pi-file-edit'
                        : child.id === 'announcement'
                        ? 'pi pi-fw pi-bell'
                        : child.id === 'warranty-dashboard'
                        ? 'pi pi-fw pi-chart-line'
                        : child.id === 'warranty-process-forms'
                        ? 'pi pi-fw pi-file'
                        : child.id === 'accounting-process'
                        ? 'pi pi-fw pi-wallet'
                        : child.id === 'warranty-reports'
                        ? 'pi pi-fw pi-chart-bar'
                        : child.id === 'warranty-approval-management'
                        ? 'pi pi-fw pi-thumbs-up'
                        : child.id === 'warranty-management-settings'
                        ? 'pi pi-fw pi-cog'
                        : child.id === 'dealer-warehouse'
                        ? 'pi pi-fw pi-box'
                        : child.id === 'external-vehicle-definition'
                        ? 'pi pi-fw pi-car'
                        : child.id === 'user-accounts'
                        ? 'pi pi-fw pi-users'
                        : child.id === 'customer-definition'
                        ? 'pi pi-fw pi-id-card'
                        : child.id === 'dealer-stock-report'
                        ? 'pi pi-fw pi-chart-bar'
                        : child.id === 'dealer-package-definition'
                        ? 'pi pi-fw pi-gift'
                        : child.id === 'preAccounting'
                        ? 'pi pi-fw pi-book'
                        : child.id === 'external-item-description'
                        ? 'pi pi-fw pi-list'
                        : child.id === 'block-shelf-seq-item-description'
                        ? 'pi pi-fw pi-list'
                        : child.id === 'other-labor-price'
                        ? 'pi pi-fw pi-dollar'
                        : child.id === 'approval-orders'
                        ? 'pi pi-fw pi-check-square'
                        : child.id === 'order-pickup'
                        ? 'pi pi-fw pi-inbox'
                        : child.id === 'retail-sales'
                        ? 'pi pi-fw pi-shopping-bag'
                        : child.id === 'vehicle-sales-place-order'
                        ? 'pi pi-fw pi-shopping-cart'
                        : child.id === 'vehicle-sales-order-requests'
                        ? 'pi pi-fw pi-list'
                        : child.id === 'vehicle-sales-my-vehicle-stock'
                        ? 'pi pi-fw pi-car'
                        : child.id === 'vehicle-sales-sell-to-customer'
                        ? 'pi pi-fw pi-user-plus'
                        : child.id === 'vehicle-sales-for-sale'
                        ? 'pi pi-fw pi-tag'
                        : child.id === 'karsan-stock-vehicles-report'
                        ? 'pi pi-fw pi-truck'
                        : child.id === 'vehicle-sales-reports'
                        ? 'pi pi-fw pi-chart-bar'
                        : child.id === 'dealer-parts-return-process'
                        ? 'pi pi-fw pi-replay'
                        : child.id === 'spare-parts-order-report'
                        ? 'pi pi-fw pi-truck'
                        : child.id === 'order-list'
                        ? 'pi pi-fw pi-wallet'
                        : child.id === 'credit-card-collection' || child.id === 'credit-card-collection-screen'
                        ? 'pi pi-fw pi-credit-card'
                        : child.id === 'collection-report'
                        ? 'pi pi-fw pi-chart-bar'
                        : child.id === 'team-productivity'
                        ? 'pi pi-fw pi-chart-line'
                        : child.id === 'service-appointment'
                        ? 'pi pi-fw pi-calendar'
                        : child.id === 'lms-dashboard'
                        ? 'pi pi-fw pi-chart-pie'
                        : child.id === 'lms-my-trainings'
                        ? 'pi pi-fw pi-book'
                        : child.id === 'lms-management-pages'
                        ? 'pi pi-fw pi-cog'
                        : child.id === 'lms-library-videos'
                        ? 'pi pi-fw pi-video'
                        : child.id === 'lms-library-documents'
                        ? 'pi pi-fw pi-file-pdf'
                        : child.id === 'lms-library-news'
                        ? 'pi pi-fw pi-megaphone'
                        : child.id === 'parts-dealer-brand-match'
                        ? 'pi pi-fw pi-tags'
                        : 'pi pi-fw pi-bookmark',

                // Favori snapshot'ı için orijinal menü düğümü alanlarını koru
                menuKey: child.id,
                title: child.title,
                translationKey: child.translationKey
            };

            // Add link if available
            if (child.link) {
                childItem.to = child.link;
            } else if (child.path) {
                childItem.to = child.path;
            }

            // Process nested children if available
            if (child.children && child.children.length > 0) {
                childItem.items = transformNestedChildren(child.children);
            }

            return childItem;
        });
    };

    // Function to transform nested child menu items
    const transformNestedChildren = (children) => {
        return children.map((child) => {
            const nestedItem = {
                label: th(child.translationKey, { fallback: child.title }),
                icon: getNestedIcon(child.id), // Alt başlıklar için özel icon getir
                // Favori snapshot'ı için orijinal menü düğümü alanlarını koru
                menuKey: child.id,
                title: child.title,
                translationKey: child.translationKey
            };

            // Add link if available
            if (child.link) {
                nestedItem.to = child.link;
            } else if (child.path) {
                nestedItem.to = child.path;
            }

            // Process deeply nested children if available
            if (child.children && child.children.length > 0) {
                nestedItem.items = transformNestedChildren(child.children);
            }

            return nestedItem;
        });
    };

    // Helper function to get icons for nested menu items
    const getNestedIcon = (itemId) => {

        // Bayi Yönetim Sayfaları ikonları
        if (itemId === 'dealer-warehouse') return 'pi pi-fw pi-box'; // Ambar Tanımlamaları
        if (itemId === 'external-vehicle-definition') return 'pi pi-fw pi-car'; // Araç Tanımlama
        if (itemId === 'user-accounts') return 'pi pi-fw pi-users'; // Bayi Kullanıcı Hesapları
        if (itemId === 'customer-definition') return 'pi pi-fw pi-id-card'; // Bayi Müşteri Tanımları
        if (itemId === 'dealer-stock-report') return 'pi pi-fw pi-chart-bar'; // Bayi Stok Raporu
        if (itemId === 'dealer-package-definition') return 'pi pi-fw pi-gift'; // Paket Tanımları
        if (itemId === 'preAccounting') return 'pi pi-fw pi-book'; // Müşteri Cari İşlemleri / Ön Muhasebe
        if (itemId === 'external-item-description') return 'pi pi-fw pi-list'; // Harici Parça Tanımları
        if (itemId === 'block-shelf-seq-item-description') return 'pi pi-fw pi-list'; // Raf Tanımlamaları
        if (itemId === 'other-labor-price') return 'pi pi-fw pi-money-bill'; // Diğer Onarım İşçilik Ücreti

        // Diğer özel ikonlar
        if (itemId === 'smartphones') return 'pi pi-fw pi-bookmark';
        if (itemId === 'accessories') return 'pi pi-fw pi-car';
        if (itemId === 'customer') return 'pi pi-fw pi-user';
        if (itemId === 'customer-address') return 'pi pi-fw pi-map-marker';
        if (itemId === 'customer-address-brand') return 'pi pi-fw pi-shopping-bag';
        if (itemId === 'customer-address-price') return 'pi pi-fw pi-wallet';
        if (itemId === 'customer-order-type') return 'pi pi-fw pi-briefcase';
        if (itemId === 'pre-approval') return 'pi pi-fw pi-thumbs-up-fill';
        if (itemId === 'warranty-claims') return 'pi pi-fw pi-clone';
        if (itemId === 'service-appointment') return 'pi pi-fw pi-calendar';
        if (itemId === 'credit-card-collection') return 'pi pi-fw pi-credit-card';
        if (itemId === 'vehicle-warranty-period-main') return 'pi pi-fw pi-calendar-times';
        if (itemId === 'vehicle-additional-warranty-period') return 'pi pi-fw pi-plus-circle';
        if (itemId === 'warranty-claim-limit') return 'pi pi-fw pi-stopwatch';
        if (itemId === 'vehicle-detail-query') return 'pi pi-fw pi-search';
        if (itemId === 'vehicle-distribution-report') return 'pi pi-fw pi-file';
        if (itemId === 'team-productivity-report') return 'pi pi-fw pi-chart-bar';

        // Varsayılan olarak yuvarlak icon
        return 'pi pi-fw pi-circle';
    };

    // Helper function to get appropriate icon for menu items
    const getIconForItem = (item) => {
        // Map specific items to icons based on their id or title
        if (item.id === 'smartphones') return 'pi pi-fw pi-bookmark';
        if (item.id === 'accessories') return 'pi pi-fw pi-car';
        if (item.id === 'customer') return 'pi pi-fw pi-user';
        if (item.id === 'customer-address') return 'pi pi-fw pi-map-marker';
        if (item.id === 'customer-address-brand') return 'pi pi-fw pi-shopping-bag';
        if (item.id === 'customer-address-price') return 'pi pi-fw pi-wallet';
        if (item.id === 'customer-order-type') return 'pi pi-fw pi-briefcase';
        if (item.id === 'accfessories') return 'pi pi-fw pi-file';
        if (item.id === 'oreder-summary') return 'pi pi-fw pi-file';
        if (item.id === 'shipments-deliverie') return 'pi pi-fw pi-file';
        if (item.id === 'system-descriptions') return 'pi pi-fw pi-wrench';
        if (item.id === 'system-items') return 'pi pi-fw pi-cog';
        if (item.id === 'user-acounts') return 'pi pi-fw pi-star';
        if (item.id === 'accesdssories') return 'pi pi-fw pi-star-fill';
        if (item.id === 'announcement-type') return 'pi pi-fw pi-tags';
        if (item.id === 'model-pages') return 'pi pi-fw pi-car';
        if (item.id === 'warranty-requests') return 'pi pi-fw pi-verified';
        if (item.id === 'customer-pages') return 'pi pi-fw pi-id-card';
        if (item.id === 'reports') return 'pi pi-fw pi-chart-bar';
        if (item.id === 'system-definitions') return 'pi pi-fw pi-slack';
        if (item.id === 'user-definitions') return 'pi pi-fw pi-users';
        if (item.id === 'announcement') return 'pi pi-fw pi-megaphone';
        if (item.id === 'order-control-report') return 'pi pi-fw pi-chart-line';
        if (item.id === 'customer-address-report') return 'pi pi-fw pi-chart-pie';
        if (item.id === 'order-summary') return 'pi pi-fw pi-th-large';
        if (item.id === 'parts-balance-list') return 'pi pi-fw pi-wrench';
        if (item.id === 'shipments-deliveries') return 'pi pi-fw pi-truck';
        if (item.id === 'estimated-delivery-date') return 'pi pi-fw pi-calendar';
        if (item.id === 'pre-approval') return 'pi pi-fw pi-thumbs-up-fill';
        if (item.id === 'warranty-claims') return 'pi pi-fw pi-clone';
        if (item.id === 'service-appointment') return 'pi pi-fw pi-calendar';
        if (item.id === 'vehicle-warranty-period') return 'pi pi-fw pi-clock';
        if (item.id === 'vehicle-warranty-period-main') return 'pi pi-fw pi-calendar-times';
        if (item.id === 'vehicle-additional-warranty-period') return 'pi pi-fw pi-plus-circle';
        if (item.id === 'warranty-claim-limit') return 'pi pi-fw pi-stopwatch';
        if (item.id === 'vehicle-detail-query') return 'pi pi-fw pi-search';
        if (item.id === 'customer-definition') return 'pi pi-fw pi-users';
        if (item.id === 'dealer-vehicle-registration-form') return 'pi pi-fw pi-search';
        if (item.id === 'vehicle-sales-vehicle-acceptance-form') return 'pi pi-fw pi-file-edit';
        if (item.id === 'vehicle-sales-dealer-vehicle-registration-form') return 'pi pi-fw pi-search';
        if (item.id === 'pre-stock-inspection-checklist') return 'pi pi-fw pi-users';

        // Default icon
        return 'pi pi-fw pi-circle';
    };

    return <AppSubMenu model={model} />;
};

export default AppMenu;
